import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger.js';

import {
  AUDIO_MIXER_MAX_CHANNELS,
  AUDIO_MIXER_MIN_CHANNELS,
} from '../../../constants.js';
import type { PlayerStore } from '../../../state/player_store.js';
import { SubscribersList } from '../../../state/subscribers_list.js';
import AudioMeterProcessorUrl from './audio_meter_processor.js?url&worker';

const logger = DebugLogger.getInstance('unified_player_audio_nodes');

type ChannelGains = {
  volumeGain: number;
  leftGain: number;
  rightGain: number;
};

export class MediaPlayerAudioNodes {
  #store: PlayerStore;
  #mediaElement: HTMLAudioElement | HTMLVideoElement;
  #audioContext: AudioContext | undefined;
  #audioSourceNode: MediaElementAudioSourceNode | undefined;
  #gainNode: GainNode | undefined;
  #channelGainNodes: {
    volumeGain: GainNode;
    leftGain: GainNode;
    rightGain: GainNode;
  }[] = [];
  #audioMeterNode: AudioWorkletNode | undefined;
  #subscribersList = new SubscribersList();

  constructor(
    store: PlayerStore,
    mediaElement: HTMLAudioElement | HTMLVideoElement,
  ) {
    this.#store = store;
    this.#mediaElement = mediaElement;

    this.#subscribersList.subscribe(
      this.#store.playableMedia.$volume,
      this.#handleVolumeChange,
    );

    this.#subscribersList.subscribe(
      this.#store.playableMedia.$activeAudioChannels,
      this.#handleAudioChannelsChange,
    );

    this.#subscribersList.listen(
      this.#store.settings.$pinnedToolbarItems,
      this.#handleGraphRequirementChange,
    );
  }

  #isMixerActive() {
    const numberOfChannels =
      this.#store.playableMedia.$activeAudioChannels.get().length;

    return (
      numberOfChannels >= AUDIO_MIXER_MIN_CHANNELS &&
      numberOfChannels <= AUDIO_MIXER_MAX_CHANNELS
    );
  }

  #isMeterPinned() {
    return this.#store.settings.$pinnedToolbarItems.get().audio_meter;
  }

  // Routing the element through an AudioContext opts it out of the browser's own compensation for
  // output latency, costing A/V sync the whole sink latency (~170ms on AirPods), so only do it when
  // the graph actually does something
  #isGraphRequired() {
    return this.#isMixerActive() || this.#isMeterPinned();
  }

  #handleGraphRequirementChange = () => {
    if (this.#audioContext || !this.#store.playableMedia.$playing.get()) {
      return;
    }
    if (this.#isGraphRequired()) {
      this.start();
    }
  };

  #handleVolumeChange = (volume: number) => {
    if (this.#gainNode) {
      this.#mediaElement.muted = false;
      this.#mediaElement.volume = 1;
      this.#gainNode.gain.value = volume;
      return;
    }
    // Safari can be slow to honour a volume change on its own, see FE-4848
    this.#mediaElement.muted = volume === 0;
    this.#mediaElement.volume = volume;
  };

  #getActiveAudioChannels = () => {
    return this.#store.playableMedia.$activeAudioChannels.get();
  };

  #getAudioChannelGains = () => {
    const audioChannels = this.#getActiveAudioChannels();
    const isAnyChannelSolo = audioChannels.some((channel) => channel.isSolo);

    return audioChannels.map((channel) => {
      let volumeGain = channel.volume;
      if (isAnyChannelSolo && !channel.isSolo) {
        volumeGain = 0;
      }
      if (channel.isMuted) {
        volumeGain = 0;
      }

      const leftGain = channel.pan <= 0 ? 1 : 1 - channel.pan;
      const rightGain = channel.pan >= 0 ? 1 : 1 + channel.pan;

      return {
        volumeGain,
        leftGain,
        rightGain,
      };
    });
  };

  #handleAudioChannelsChange = () => {
    const gains = this.#getAudioChannelGains();
    gains.forEach((gain, index) => {
      this.#setChannelGain(index, gain);
    });
    this.#handleGraphRequirementChange();
  };

  #setChannelGain(channel: number, gain: ChannelGains) {
    if (this.#channelGainNodes[channel]) {
      this.#channelGainNodes[channel].volumeGain.gain.value = gain.volumeGain;
      this.#channelGainNodes[channel].leftGain.gain.value = gain.leftGain;
      this.#channelGainNodes[channel].rightGain.gain.value = gain.rightGain;
    }
  }

  async #init() {
    try {
      this.#audioContext = new AudioContext();
      await this.#audioContext.audioWorklet.addModule(
        new URL(AudioMeterProcessorUrl, import.meta.url),
      );
      this.#audioSourceNode = this.#audioContext.createMediaElementSource(
        this.#mediaElement,
      );
      this.#connectNodes();
      this.#subscribersList.subscribe(
        this.#store.playableMedia.$playing,
        (playing) => {
          if (playing) {
            this.start();
            return;
          }
          this.pause();
        },
      );
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: debug
      console.error('Failed to start audio context', error);
    }
  }

  start = async () => {
    try {
      if (!this.#audioContext && !this.#isGraphRequired()) {
        logger.debug(
          'Audio graph not required, leaving audio on the native path',
        );
        return;
      }
      logger.debug('Starting audio context', this.#audioContext);
      if (!this.#audioContext || this.#audioContext.state === 'closed') {
        await this.#init();
        if (!this.#audioContext) {
          return;
        }
      }
      // Ensure the audio context is resumed.
      if (this.#audioContext.state === 'suspended') {
        logger.debug('Resuming audio context');
        await this.#audioContext.resume();
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: debug
      console.error('Failed to start audio context', error);
    }
  };

  pause = () => {
    if (this.#audioContext?.state === 'running') {
      this.#audioContext.suspend().catch((error) => {
        // biome-ignore lint/suspicious/noConsole: debug
        console.error('Failed to suspend audio context', error);
      });
    }
  };

  destroy() {
    this.pause();

    this.#destroyChannelNodes();

    this.#gainNode?.disconnect();
    this.#gainNode = undefined;

    if (this.#audioMeterNode) {
      this.#audioMeterNode.port.removeEventListener(
        'message',
        this.#handleAudioProcess,
      );
      this.#audioMeterNode.disconnect();
      this.#audioMeterNode = undefined;
    }

    this.#audioSourceNode?.disconnect();
    this.#audioSourceNode = undefined;

    this.#audioContext?.close();
    this.#audioContext = undefined;

    this.#subscribersList.unsubscribeAll();
  }

  #unwrapAudioContext() {
    if (!this.#audioContext) {
      throw new Error('Audio context is not initialized');
    }
    return this.#audioContext;
  }

  #unwrapAudioSourceNode() {
    if (!this.#audioSourceNode) {
      throw new Error('Audio source node is not initialized');
    }
    return this.#audioSourceNode;
  }

  #handleAudioProcess = (event: MessageEvent) => {
    const dbValues = event.data;
    this.#store.playableMedia.setAudioMeter(dbValues);
  };

  #destroyChannelNodes() {
    this.#channelGainNodes.forEach((channelGainNodes) => {
      channelGainNodes.leftGain.disconnect();
      channelGainNodes.rightGain.disconnect();
      channelGainNodes.volumeGain.disconnect();
    });
    this.#channelGainNodes = [];
  }

  #connectChannelNodes({
    sourceNode,
    destinationNode,
    audioMeterNode,
  }: {
    sourceNode: AudioNode;
    destinationNode: AudioNode;
    audioMeterNode: AudioNode;
  }) {
    this.#destroyChannelNodes();
    const audioContext = this.#unwrapAudioContext();
    const audioChannels = this.#store.playableMedia.$activeAudioChannels.get();
    const numberOfChannels = audioChannels.length;
    if (
      numberOfChannels < AUDIO_MIXER_MIN_CHANNELS ||
      numberOfChannels > AUDIO_MIXER_MAX_CHANNELS
    ) {
      logger.debug(
        'Playlist item does not contain multi-channel audio, channels:',
        numberOfChannels,
      );
      sourceNode.connect(destinationNode);
      sourceNode.connect(audioMeterNode);
      return;
    }

    logger.debug(`Constructing ${numberOfChannels} channel gain nodes`);
    const splitterNode = new ChannelSplitterNode(audioContext, {
      numberOfOutputs: numberOfChannels,
    });
    const mergerNode = new ChannelMergerNode(audioContext, {
      numberOfInputs: 2,
      channelInterpretation: 'discrete',
    });
    const allChannelsMergerNode = new ChannelMergerNode(audioContext, {
      numberOfInputs: numberOfChannels,
      channelInterpretation: 'discrete',
    });

    sourceNode.connect(splitterNode);
    const gains = this.#getAudioChannelGains();
    for (let index = 0; index < numberOfChannels; index++) {
      const channelLeftGainNode = new GainNode(audioContext, {
        gain: gains[index].leftGain,
      });
      const channelRightGainNode = new GainNode(audioContext, {
        gain: gains[index].rightGain,
      });
      const channelGainNode = new GainNode(audioContext, {
        gain: gains[index].volumeGain,
      });
      this.#channelGainNodes.push({
        volumeGain: channelGainNode,
        leftGain: channelLeftGainNode,
        rightGain: channelRightGainNode,
      });

      // Connect the splitter output to the gain node
      splitterNode.connect(channelGainNode, index, 0);

      // Connect the gain node to the left and right gain nodes
      channelGainNode.connect(channelLeftGainNode);
      channelGainNode.connect(channelRightGainNode);

      // Connect the left and right gain nodes to the merger
      channelLeftGainNode.connect(mergerNode, 0, 0);
      channelRightGainNode.connect(mergerNode, 0, 1);

      channelGainNode.connect(allChannelsMergerNode, 0, index);
    }
    mergerNode.connect(destinationNode);
    allChannelsMergerNode.connect(audioMeterNode);
  }

  #connectNodes() {
    const audioSourceNode = this.#unwrapAudioSourceNode();
    const audioContext = this.#unwrapAudioContext();

    try {
      this.#mediaElement.volume = 1;
      this.#gainNode = new GainNode(audioContext, {
        gain: this.#store.playableMedia.$volume.get(),
      });
      this.#audioMeterNode = new AudioWorkletNode(
        audioContext,
        'audio-meter-processor',
      );
      this.#connectChannelNodes({
        sourceNode: audioSourceNode,
        destinationNode: this.#gainNode,
        audioMeterNode: this.#audioMeterNode,
      });

      this.#gainNode.connect(audioContext.destination);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: debug
      console.error('Failed to connect nodes', error);
    }
    this.#audioMeterNode?.port.addEventListener(
      'message',
      this.#handleAudioProcess,
    );
    this.#audioMeterNode?.port.start();
  }
}
